import { QAppVersionsMostCriticalCvesCard, QMostCommonCriticalCvesCard, QRiskExposureCard } from '@kw/quokka-ui';
import { Box } from '@mui/material';
import { DashboardAnalyticsDateRangeDays, DashboardAnalyticsMetrics } from './dashboardAnalytics';
import { useDashboardNavigation } from './useDashboardNavigation';

type SbomAnalysisProps = {
  dateRangeDays: DashboardAnalyticsDateRangeDays;
  metrics: DashboardAnalyticsMetrics | null | undefined;
};

const platforms = ['android', 'ios'] as const;

export const SbomAnalysis = ({ dateRangeDays, metrics }: SbomAnalysisProps) => {
  const { getApplicationsHref, getAppReportHref } = useDashboardNavigation(dateRangeDays);

  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: 'repeat(6, 1fr)',
        gap: '24px',
        '& .MuiPaper-root': { width: '100%' },
      }}
    >
      {/* Row 1: SBOM Risk Exposure by CVSS Score — combined platforms, full width */}
      <Box sx={{ gridColumn: 'span 6' }}>
        <QRiskExposureCard
          data={metrics?.riskExposureByCvss ?? []}
          getCveHref={item => getApplicationsHref({ cveId: item.cveId, cveSeverity: item.severity, excludeTrusted: true })}
        />
      </Box>

      {/* Row 2: App Versions with most SBOM Critical CVEs — Android + iOS side-by-side */}
      {platforms.map(platform => (
        <Box key={`sbom-app-versions-${platform}`} sx={{ gridColumn: 'span 3' }}>
          <QAppVersionsMostCriticalCvesCard
            platform={platform}
            data={(platform === 'android' ? metrics?.appVersionsMostCriticalCvesAndroid : metrics?.appVersionsMostCriticalCvesIos) ?? []}
            getAppHref={item => getAppReportHref(item.analysisId, { focusSbomTab: true })}
          />
        </Box>
      ))}

      {/* Row 3: Most Commonly Found SBOM Critical CVEs — Android + iOS side-by-side */}
      {platforms.map(platform => (
        <Box key={`sbom-common-cves-${platform}`} sx={{ gridColumn: 'span 3' }}>
          <QMostCommonCriticalCvesCard
            platform={platform}
            data={(platform === 'android' ? metrics?.mostCommonCriticalCvesAndroid : metrics?.mostCommonCriticalCvesIos) ?? []}
            getCveHref={item => getApplicationsHref({ platform, cveId: item.cveId, cveSeverity: 'critical', excludeTrusted: true })}
          />
        </Box>
      ))}
    </Box>
  );
};
